import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Video } from 'lucide-react';
import CTA from '../../components/CTA';

const MagherafeltHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Magherafelt | Online Sessions from Belfast Clinic</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Magherafelt, Mid Ulster. Online sessions recommended - no travel required. Farming community stress, rural isolation, market town business support. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Magherafelt, hypnotherapist Magherafelt Mid Ulster, online hypnotherapy County Londonderry, farming community mental health, rural anxiety support" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/magherafelt-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Magherafelt | Online Sessions from Belfast Clinic" />
        <meta property="og:description" content="Professional hypnotherapy serving Magherafelt, Mid Ulster. Online sessions - professional support without the drive." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Magherafelt",
            "description": "CNHC registered hypnotherapy serving Magherafelt, County Londonderry from our Belfast clinic. Specializing in rural isolation support, farming community stress, market town business pressures, anxiety management, and confidential mental health services for Mid Ulster.",
            "url": "https://www.sharonclare-hypnotherapy.com/magherafelt-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Magherafelt",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Londonderry"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Rural Isolation Support",
              "Farming Community Stress",
              "Market Town Business Pressures",
              "Confidential Mental Health Support",
              "Online Hypnotherapy Sessions"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Magherafelt
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions from home, plus the Belfast and Lisburn clinics
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <Video className="w-6 h-6 text-accent-600" />
                <p className="text-lg font-semibold text-gray-900">
                  Online sessions available any day — no travel from Magherafelt
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-600">
                <MapPin className="w-5 h-5 text-gray-500" />
                <p className="text-base">
                  Or in person at the Belfast clinic on the Ormeau Road, or the Lisburn clinic on Antrim Street
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Magherafelt and Mid Ulster Market Town Communities
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I'm delighted to serve this traditional Mid Ulster market town community. For Magherafelt residents, <strong>online video sessions</strong> are usually the most practical option — available throughout the week and providing professional hypnotherapy support from the comfort of home. If you would rather meet face to face, I hold clinics at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, and at The Arzen Collective, 13B Antrim Street, Lisburn, with days and times that vary week to week.
              </p>
              <p>
                Magherafelt is a small market town with a population of approximately 9,000 people, characterized by its traditional Ulster market town identity and deep agricultural heritage. Wednesday is market day, a reminder of the town's historic role as a trading center for surrounding farming communities. The pace of life here is notably different from large Belfast commuter towns - quieter, more rural, with stronger connections to agricultural traditions and close-knit community networks where people know their neighbors.
              </p>
              <p>
                This distinctive character creates specific mental health challenges that differ significantly from urban or commuter town stressors. Rural isolation and distance from specialist services mean that professional mental health support can feel inaccessible. The farming community faces unique pressures - financial uncertainty tied to weather and market forces, physical isolation during busy agricultural seasons, and the mental health challenges that come with self-employed rural work. Market town businesses struggle with competition from larger towns, changing retail patterns, and economic uncertainty. And in a small, close-knit community where everyone knows everyone, there can be genuine concerns about privacy and confidentiality when seeking mental health support.
              </p>
              <p>
                This is where online hypnotherapy becomes particularly valuable for Magherafelt residents. Professional, CNHC-registered hypnotherapy delivered via secure video sessions means you can access evidence-based mental health support from the comfort and privacy of your own home, without the approximately 40-mile journey to our Belfast clinic. For farming families with demanding schedules, small business owners with limited time, or anyone who values the convenience of professional support without extensive travel, online sessions offer genuine accessibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Online Sessions Work for Magherafelt */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Why Online Sessions Work Well for Magherafelt Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      No Travel Time Required
                    </h3>
                    <p className="text-gray-700">
                      At approximately 40 miles from the Belfast clinic, in-person sessions mean around 90 minutes of round-trip driving. Online sessions eliminate this entirely - professional hypnotherapy from your home in Magherafelt without any travel. Time is precious in farming and small business life; online sessions respect that reality.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Privacy and Confidentiality
                    </h3>
                    <p className="text-gray-700">
                      In a small market town where people know each other, seeking mental health support can feel exposing. Online sessions from your own home provide complete privacy - no one sees you entering a clinic, no questions about where you're going, just confidential professional support.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <MapPin className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Flexibility for Farming Schedules
                    </h3>
                    <p className="text-gray-700">
                      Agricultural work doesn't follow 9-5 schedules. Calving season, harvest, silage - farming demands are unpredictable. Online sessions are easier to fit around farming life than committing to drive 20+ miles at a fixed time. Schedule during quieter periods without worrying about travel logistics.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Video className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Equally Effective Treatment
                    </h3>
                    <p className="text-gray-700">
                      Hypnotherapy works through voice, guidance, and your own subconscious processes - not physical presence. Online sessions via secure video are equally effective as in-person. You receive the same professional, CNHC-registered treatment with the convenience of staying in Magherafelt.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-white rounded-xl p-6 border-2 border-accent-200">
              <p className="text-gray-700 mb-2">
                <strong>In-person sessions available if preferred:</strong> While online is recommended due to distance, in-person appointments are available at our Belfast clinic (Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ), with days and times that vary week to week. It is approximately 40 miles via the M22/M2, taking 45-55 minutes. Both options provide the same professional treatment - choose what works best for your circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Magherafelt Community */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Services Tailored to Magherafelt's Rural Market Town Community
            </h2>
            <p className="text-lg text-gray-700 mb-12">
              Living in a small Mid Ulster market town brings distinct mental health challenges that differ fundamentally from urban or large commuter town experiences. Rural isolation and limited access to specialist services, the unique stresses facing farming families and agricultural communities, pressures on small market town businesses, the challenge of maintaining privacy in close-knit communities where everyone knows everyone, and the mental health impacts of rural economic uncertainty - these are the specific realities of Magherafelt life. Hypnotherapy addresses these challenges with evidence-based techniques, delivered accessibly via online sessions.
            </p>

            <div className="space-y-10">
              {/* Rural Isolation and Mental Health Access */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Rural Isolation and Mental Health Access
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    One of the most significant mental health challenges facing Magherafelt and rural Mid Ulster communities is simply accessing professional support. Mental health services are predominantly concentrated in larger towns and cities - Derry or Belfast primarily. For a Magherafelt resident struggling with anxiety, depression, or stress, the nearest specialist support might be 40+ miles away, requiring significant time and travel that many simply cannot manage around work, family, or farming commitments. NHS waiting times for psychological therapies are often 6-12 months or longer. This geographical isolation from services can leave people feeling trapped with their mental health struggles.
                  </p>
                  <p>
                    Rural isolation also has a psychological dimension beyond distance from services. Living in a small market town or surrounding rural areas can mean limited social connections, especially for those who don't fit the traditional farming or business community molds. Young people often leave for Belfast or Derry for education and careers, taking social networks with them. Older adults and farming families can experience profound isolation, going days without meaningful social interaction beyond essential transactions. Winter months particularly exacerbate this - dark evenings, agricultural slowdown, limited activities, and the sense of being cut off from the broader world.
                  </p>
                  <p>
                    Online hypnotherapy directly addresses the accessibility barrier. Professional, CNHC-registered mental health support becomes available from your home in Magherafelt, without the 40+ mile drive to Belfast or extensive waiting times for NHS services. For anxiety, depression, stress, or other mental health concerns, you can access evidence-based treatment within days rather than months, fitting sessions around farming schedules or business commitments, all from the privacy and comfort of your own space.
                  </p>
                  <p>
                    The approach is particularly effective for rural clients who value self-reliance and practical solutions over lengthy psychological exploration. Solution-focused hypnotherapy focuses on moving forward, building resilience, and creating practical change - aligning well with rural community values while providing the professional mental health support that geographical isolation might otherwise prevent.
                  </p>
                </div>
              </div>

              {/* Farming Community Stress and Agricultural Pressures */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Farming Community Stress and Agricultural Mental Health
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    The Magherafelt area has deep agricultural roots, with many families engaged in farming or agricultural services. While farming life has many rewards, it also creates unique and intense mental health pressures that are poorly understood outside agricultural communities. Financial stress is tied directly to factors beyond your control - weather, disease outbreaks, market prices, political decisions affecting subsidies. A wet summer ruins silage quality; a disease outbreak devastates livestock; Brexit changes subsidies overnight. Years of work can be wiped out by circumstances you cannot prevent, creating chronic anxiety about survival.
                  </p>
                  <p>
                    Physical isolation during busy seasons (calving, lambing, harvest, silage) means farmers can go weeks working 16-hour days with minimal sleep and virtually no social interaction. The work is relentless - animals don't take days off, emergencies happen at 3am, and there's no clocking out at 5pm. Multi-generational family farms bring additional pressure: maintaining family legacy, living up to parents' or grandparents' standards, navigating difficult family dynamics when working alongside relatives daily, and the weight of knowing failure affects family not just yourself.
                  </p>
                  <p>
                    Mental health stigma remains particularly strong in agricultural communities. The cultural expectation to be tough, self-reliant, and uncomplaining means farmers often suffer in silence with depression, anxiety, or exhaustion rather than seeking help that might be perceived as weakness. Financial pressures make paying for mental health support feel impossible when farm income is uncertain. And finding time for therapy when animals need feeding and work is constant feels impractical.
                  </p>
                  <p>
                    Online hypnotherapy addresses these barriers practically. Sessions can be scheduled during quieter periods (winter months, late evenings) without requiring travel time that working farmers simply don't have. The privacy of online sessions means no one needs to know you're accessing support - important in close-knit agricultural communities. Hypnotherapy helps manage chronic stress and anxiety, improve sleep quality despite irregular hours, process grief and loss (whether livestock, financial, or identity-related), build resilience against factors beyond your control, and develop sustainable coping strategies for the unique pressures of agricultural life. Farming mental health matters - your wellbeing is essential for the farm's survival, not a luxury you can't afford.
                  </p>
                </div>
              </div>

              {/* Market Town Business Pressures */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Market Town Business Stress and Economic Uncertainty
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Magherafelt's identity as a traditional market town means it hosts numerous small, family-run businesses - shops, cafés, agricultural suppliers, services, and trades. Running a small business in a market town brings particular challenges distinct from urban business environments. Competition from larger towns (Derry, Belfast) with greater retail choice pulls customers away. Online shopping erodes local retail, making survival difficult for traditional shops. Economic uncertainty affects customer spending - when farming income drops or jobs are uncertain, local businesses feel the impact directly.
                  </p>
                  <p>
                    Small business owners carry enormous stress: financial responsibility for staff livelihoods, personal investment (often mortgaging homes to fund businesses), working constantly without guaranteed income, wearing multiple hats (owner, accountant, marketer, stock manager, cleaner), and the pressure of maintaining family legacies when businesses have been in families for generations. There's no separation between work and life - the business IS your life, consuming thoughts constantly, creating chronic stress that never switches off.
                  </p>
                  <p>
                    In a small market town, there's also the social pressure of visibility. Your business struggles are known to the community; failure feels more public and shameful than in anonymous urban environments. Customer relationships are personal - these aren't strangers, they're neighbors, friends, relatives - making difficult business decisions (price increases, staff cuts, closing) emotionally complex.
                  </p>
                  <p>
                    Hypnotherapy helps market town business owners manage: chronic stress and anxiety about business survival, decision-making anxiety (fear of making wrong choices), sleep problems from constant business worries, imposter syndrome ("am I capable of running this?"), processing grief if closure becomes necessary, building resilience against economic factors beyond control, and developing sustainable stress management strategies that recognize business demands while protecting mental health. Online sessions respect the time constraints of business ownership - no closing shop for travel, just professional support from your office or home when it suits your schedule.
                  </p>
                </div>
              </div>

              {/* Confidential Support Outside Close-Knit Community Networks */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Confidential Mental Health Support in Small-Town Communities
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Living in a small market town like Magherafelt (population ~9,000) creates a community dynamic fundamentally different from urban anonymity. People know each other, recognize cars, notice comings and goings. This close-knit quality can be wonderful for support and belonging, but it creates genuine barriers to seeking mental health help. There's a realistic concern: "What if someone sees me at the clinic?" "What if word gets around?" "What will people think?" In a community where everyone knows everyone, privacy can feel impossible.
                  </p>
                  <p>
                    This visibility creates particular challenges for those struggling with mental health. Anxiety about social judgment ("people will think I'm weak"), concern about professional reputation (business owners, teachers, community leaders worrying about public perception), family shame and stigma (older generations' attitudes toward mental health), and simply the exhaustion of maintaining a "fine" facade when you're struggling but can't let the community see it.
                  </p>
                  <p>
                    Online hypnotherapy provides genuine confidentiality. Sessions from your own home mean no one knows you're accessing support. No car parked outside a clinic for neighbors to see. No explaining where you're going. No risk of bumping into someone you know in a waiting room. This privacy isn't about shame - it's about practical reality in small communities where mental health stigma persists despite growing awareness.
                  </p>
                  <p>
                    For Magherafelt residents who need professional mental health support but genuinely worry about community judgment, online sessions provide the confidentiality that makes accessing help possible. Your mental health struggles don't define you, and you deserve support without needing to broadcast your private challenges to your entire community. Professional, CNHC-registered hypnotherapy delivered privately, discreetly, and effectively - addressing your actual needs while respecting the social reality of small-town life.
                  </p>
                </div>
              </div>

              {/* General Anxiety and Stress */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  General Anxiety and Stress Management
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Beyond specific circumstances, many Magherafelt residents experience generalized anxiety - persistent worry that feels out of proportion, racing thoughts that won't quiet, physical tension (tight shoulders, clenched jaw), sleep disturbances (lying awake at 2am worrying about everything and nothing), and the overwhelming feeling of barely coping. Rural life brings particular anxiety triggers: financial uncertainty in farming or small business contexts, isolation and limited social support, concern about young people leaving (aging community), worry about accessing healthcare when distant from services, and the pace of change affecting traditional market town life.
                  </p>
                  <p>
                    Anxiety often manifests physically - headaches, digestive problems, muscle pain, exhaustion that isn't relieved by sleep. Many people describe feeling constantly on edge, waiting for the next crisis, unable to relax even when there's objectively nothing wrong. The worry becomes automatic and all-consuming.
                  </p>
                  <p>
                    Hypnotherapy addresses anxiety at the subconscious level where automatic worry patterns are stored. Clinical hypnosis helps retrain the nervous system's threat response, reducing the constant background tension. Techniques include: progressive relaxation to release physical stress held in the body, cognitive reframing to challenge catastrophic thinking patterns, visualization to create felt safety in your nervous system, anchoring techniques to access calm states during stressful moments, and improving sleep architecture for genuine rest. Research shows hypnotherapy can be as effective as CBT for anxiety, with clients often noticing significant improvement within 4-6 sessions. For those who find traditional talk therapy too slow or abstract, hypnotherapy works directly with the emotional brain where anxiety originates.
                  </p>
                </div>
              </div>

              {/* Additional Services */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Depression and Seasonal Affective Disorder
                  </h4>
                  <p className="text-gray-700">
                    Rural isolation, winter darkness, agricultural pressures, and limited social connection can contribute to depression. Seasonal affective disorder is common in rural areas with dark winters. Hypnotherapy complements medical treatment, helping improve mood, energy, motivation, and connection to meaning and purpose.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Sleep Problems and Insomnia
                  </h4>
                  <p className="text-gray-700">
                    Farming early starts, business worries, rural isolation anxiety - many factors disrupt sleep in market town communities. Racing thoughts keeping you awake? Waking at 4am worrying? Hypnotherapy helps quiet mental chatter and retrain your nervous system for restful sleep essential for demanding rural life.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Smoking Cessation and Habit Change
                  </h4>
                  <p className="text-gray-700">
                    Ready to quit smoking? Break stress-eating patterns? Address anxiety-driven habits? Hypnotherapy targets the subconscious drivers of automatic behaviors - addressing why you smoke/eat rather than just stopping. More effective than willpower alone, particularly for stress-related habits.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Grief and Loss Processing
                  </h4>
                  <p className="text-gray-700">
                    Farm losses, business closure, bereavement, young people leaving - rural communities experience specific losses. Hypnotherapy helps process grief, find meaning, and move forward while honoring what's been lost. Particularly helpful when traditional grief support feels inaccessible or inadequate for rural contexts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Accessing Hypnotherapy from Magherafelt
            </h2>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Video className="w-6 h-6 text-accent-600 mr-3" />
                Online Sessions (Recommended)
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-semibold">Professional hypnotherapy from your home in Magherafelt - no travel required</p>
                <p>
                  <strong>How it works:</strong> Sessions conducted via secure video platform (Zoom). You need a private, quiet space, reliable internet connection, and a device with camera/microphone (computer, tablet, or smartphone).
                </p>
                <p>
                  <strong>Why online works well for Magherafelt:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>No 40-50 minute round trip on rural roads</li>
                  <li>Easier to fit around farming schedules and business commitments</li>
                  <li>Complete privacy - no one sees you accessing mental health support</li>
                  <li>Same therapeutic effectiveness as in-person sessions</li>
                  <li>Schedule flexibility - evening and weekend appointments available</li>
                </ul>
                <p className="text-sm italic">
                  Note: Hypnotherapy works through voice, guidance, and your subconscious processes - not physical presence. Online sessions are equally effective as in-person for most clients, with significantly greater convenience for rural communities.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Car className="w-6 h-6 text-accent-600 mr-3" />
                In-Person at Belfast Clinic (If Preferred)
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-semibold">Distance: approximately 40 miles | Drive time: 45-55 minutes via the M22/M2</p>
                <p><strong>Route:</strong></p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>From Magherafelt, take the A31 east toward Castledawson</li>
                  <li>Join the M22 then the M2 into Belfast</li>
                  <li>Head south through the city onto the Ormeau Road (A24)</li>
                  <li>Clinic: Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ - south Belfast, about 10 minutes from the city centre</li>
                </ol>
                <p><strong>By public transport:</strong> Botanic and City Hospital are the closest rail stations to the clinic, with Lanyon Place serving regional routes. Metro bus services run along the Ormeau Road corridor.</p>
                <p>
                  <strong>Clinic days and times:</strong> These vary week to week. Current availability and online booking are on the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-700 font-medium underline"
                  >
                    clinic booking page
                  </a>
                  . Early evening appointments are available.
                </p>
                <p className="text-sm italic">
                  Note: While in-person sessions are available for those who prefer face-to-face appointments, the approximately 40-mile distance makes online sessions more practical for most Magherafelt residents. Both options provide the same professional, CNHC-registered treatment.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                Distance Transparency
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  I believe in being honest about distances. Magherafelt is approximately 40 miles from our Belfast clinic on the Ormeau Road. While this is manageable for occasional visits, it represents around 90 minutes of round-trip travel on rural and motorway roads. For regular weekly hypnotherapy sessions, this travel commitment can become a barrier to consistency.
                </p>
                <p>
                  <strong>Online sessions solve this practical problem.</strong> You receive the same professional treatment, the same CNHC-registered expertise, the same evidence-based techniques - but from your home in Magherafelt without any travel time. For farming families, small business owners, or anyone with limited time and energy, this accessibility makes the difference between accessing mental health support and continuing to struggle without help.
                </p>
                <p>
                  Professional mental health support shouldn't require extensive rural travel. Online hypnotherapy brings that support to you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Questions from Magherafelt Clients
            </h2>
            <div className="space-y-6">

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online hypnotherapy sessions as effective as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes - hypnotherapy works through voice, guidance, and your own subconscious processes, not physical presence in a room. The therapeutic techniques (relaxation, visualization, suggestion, reframing) are delivered identically whether online or in-person. Research shows online hypnotherapy is equally effective, with the added benefits of convenience, privacy, and no travel time. Many rural clients find online sessions more effective precisely because they eliminate the stress and time burden of traveling 20+ miles each week.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How far is Magherafelt from your clinic?
                </h3>
                <p className="text-gray-700">
                  Magherafelt is approximately 40 miles from our Belfast clinic (Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ) via the M22/M2 and then south onto the Ormeau Road, taking 45-55 minutes by car. While in-person sessions are available in south Belfast for those who prefer them, this distance is why I recommend online sessions as the primary option for Magherafelt residents - same professional treatment without the 90-minute round trip.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you understand farming community mental health challenges?
                </h3>
                <p className="text-gray-700">
                  Yes. I work with farming families throughout Mid Ulster and understand the unique pressures: financial stress tied to weather and markets, physical isolation during busy seasons, relentless work with no days off, multi-generational farm dynamics, mental health stigma in agricultural communities, and the practical barriers to accessing support. Hypnotherapy helps manage chronic stress and anxiety, improve sleep despite irregular hours, process grief and losses, build resilience against factors beyond your control, and develop sustainable coping strategies. Online sessions respect farming schedules - no leaving animals to travel 20+ miles.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Will online sessions be private in a small town where people know each other?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Online sessions from your own home mean complete privacy - no one sees you entering a clinic, no explaining where you're going, no risk of bumping into neighbors in a waiting room. This confidentiality is particularly important in small market town communities where mental health stigma persists and lack of anonymity can prevent people seeking help they genuinely need. Your sessions are conducted via secure video platform, entirely confidential, and no one needs to know you're accessing mental health support unless you choose to share that.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can you help with market town small business stress?
                </h3>
                <p className="text-gray-700">
                  Yes. Small business owners in market towns face unique pressures - competition from larger towns and online shopping, financial responsibility for staff, economic uncertainty, personal investment (often mortgaging homes), working constantly without guaranteed income, and the visibility of business struggles in small communities where everyone knows everyone. Hypnotherapy helps manage chronic business stress and anxiety, improve decision-making confidence, address sleep problems from constant worrying, process grief if closure becomes necessary, and develop sustainable stress management that protects your mental health while acknowledging business demands.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What if I don't have good internet for video sessions?
                </h3>
                <p className="text-gray-700">
                  Video sessions require stable internet connection, but don't need particularly high speed - similar to watching a YouTube video. Most Magherafelt homes and businesses have sufficient broadband. If internet is genuinely inadequate, in-person sessions at our Belfast clinic (Belfast Wellness Clinic, 228 Ormeau Road, approximately 40 miles) remain available as an option. During booking, we can test your connection to ensure video sessions will work smoothly before committing to this approach.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies by individual and concern. Most clients notice improvement within 4-6 sessions for issues like anxiety, stress, or sleep problems. Some concerns (specific phobias, smoking cessation) may respond in 1-3 sessions. Deeper patterns (chronic anxiety from farming stress, long-term depression, complex grief) may benefit from 8-12 sessions. We'll discuss your specific situation during the free initial consultation and create a realistic plan. You're always in control and can stop whenever you feel you've achieved what you needed.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy covered by health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance plans cover hypnotherapy if provided by a CNHC-registered practitioner (which I am). Check your policy or contact your insurer. If not covered, discounted packages are available when you book multiple sessions. For farming families or small business owners concerned about cost, consider this: untreated anxiety, depression, or stress costs far more - in lost productivity, physical health problems, medication, relationship damage, and quality of life. Investing in mental health is investing in your ability to sustain your work and life.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What equipment do I need for online sessions?
                </h3>
                <p className="text-gray-700">
                  You need: (1) A device with camera and microphone - computer, laptop, tablet, or smartphone all work; (2) Stable internet connection (broadband or good mobile data); (3) A private, quiet space where you won't be interrupted for 60 minutes; (4) Headphones/earbuds recommended but not essential. Sessions use Zoom or similar secure video platform - simple to use, no technical expertise required. I'll provide clear instructions before your first session.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are you qualified and registered?
                </h3>
                <p className="text-gray-700">
                  Yes - I'm registered with the CNHC (Complementary and Natural Healthcare Council), the UK regulatory body for hypnotherapists accredited by the Professional Standards Authority. This requires 450+ hours of clinical training, ongoing professional development, full insurance, and adherence to strict ethical standards. You can verify my registration at www.cnhc.org.uk. I also have 15 years of nursing experience at Belfast City Hospital, bringing professional healthcare understanding to hypnotherapy practice. You're receiving treatment from a qualified, regulated professional, not an unregulated practitioner.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What Clients from Mid Ulster Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The farming stress was destroying my mental health - financial worry, isolation during calving, never switching off. Sharon's online sessions were a lifeline. I could fit them around farm work without driving down to Belfast. The privacy was important too - no one in our small community needed to know. My anxiety has reduced dramatically and I'm sleeping properly for the first time in years."
                </p>
                <p className="text-gray-900 font-semibold">— P.M., Mid Ulster (farming family)</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Running a small shop in Magherafelt, the business stress was overwhelming - competition from online shopping, financial worry, working 60 hours a week. Online hypnotherapy let me access professional help without closing the shop to travel. Sharon understood market town business pressures. I'm managing the stress better and have made peace with doing my best in difficult circumstances."
                </p>
                <p className="text-gray-900 font-semibold">— L.D., Magherafelt (small business owner)</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The rural isolation was getting to me - young people leaving, limited social connections, dark winters. I was hesitant about online therapy but Sharon made it easy. The sessions helped tremendously with depression and loneliness. Having professional mental health support without traveling 40 miles to Belfast made all the difference. I'm so grateful this was accessible."
                </p>
                <p className="text-gray-900 font-semibold">— K.S., County Londonderry (rural area)</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I quit smoking after 20 years with Sharon's help via online sessions - took 5 sessions. The convenience of doing it from home in Magherafelt without traveling was essential. Sharon's approach is professional and evidence-based. I'm saving hundreds of pounds a month and my health has improved noticeably. Highly recommend to anyone in Mid Ulster."
                </p>
                <p className="text-gray-900 font-semibold">— J.W., Maghera</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The generalized anxiety I'd lived with for years reduced dramatically after 6 online sessions. Sharon understood the specific pressures of living in a small market town - privacy concerns, limited services, rural isolation. The online format was perfect for fitting around my work. Professional, effective, and genuinely accessible for rural Mid Ulster."
                </p>
                <p className="text-gray-900 font-semibold">— R.H., Magherafelt</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  );
};

export default MagherafeltHypnotherapy;
